/**
 * "Keep what rendered" — the rule that lets a half-typed Twig expression sit in the
 * editor without emptying the preview under it.
 *
 * ### The problem it solves
 *
 * `renderTwigPreview` never throws: when twig.js cannot parse the source it returns the
 * *naive substitution* of the raw body instead, with `error` set. For a hand-written
 * body that is a reasonable consolation prize — the words are still there. For a
 * compiled visual document it is not: that body is `{% block %}` overrides full of
 * `{% include 'core.…' %}`, so the unrendered form has no layout, no components and no
 * `data-cvn-id` markers. The canvas goes blank and the selection chrome with it.
 *
 * And it takes exactly two keystrokes to get there. `{{` — the thing you type to open
 * the variable list — is an unterminated output tag, so twig.js throws
 * *"Unable to find closing bracket '}}'"* from the moment the second brace lands until
 * the expression is finished. Owner-reported: *"the entire content tends to disappear …
 * it would be better if the template simply does not re-render on invalid twig so I can
 * calmly finish typing."*
 *
 * ### The rule
 *
 * A render that failed is not a new picture of the email — it is *no* picture of it. So
 * the last one that worked stays on screen, marked `stale`, and the error travels
 * alongside it. The preview freezes for the few hundred milliseconds it takes to type
 * `customer.firstname }}` and picks up again on the next source that parses.
 *
 * Applied at the point of *display* rather than inside `renderTwigPreview`, which is a
 * pure function used for extraction as well as for pictures, and whose fallback output
 * is genuinely wanted by the callers that read `placeholderVariables` out of a body
 * nobody is currently typing into.
 */
import { useMemo, useRef } from 'react';
import { TwigPreviewResult } from './twigRenderer';

export interface StableTwigPreviewResult extends TwigPreviewResult {
  /**
   * The `html` is an earlier render, kept because the current source does not parse.
   * Callers that draw geometry over the preview are unaffected — the markers belong to
   * the HTML actually on screen — but anything phrased as "this email" should say
   * *last working version* while this is set.
   */
  stale: boolean;
}

/**
 * The latest renderable result: `fresh` while it renders, the previous one while it
 * does not.
 *
 * The ref is written during render, which is safe here because the write is a pure
 * function of the argument — a double-invoked render stores the same value twice.
 * `useEffect` would be a frame too late and show the blank canvas this exists to
 * prevent.
 *
 * Before the *first* successful render there is nothing to keep, so the fallback shows
 * as it always did: a template that has never rendered is not one the user is midway
 * through editing.
 */
export function useLastGoodRender(
  fresh: TwigPreviewResult,
): StableTwigPreviewResult {
  const lastGood = useRef<TwigPreviewResult | null>(null);
  if (!fresh.error) lastGood.current = fresh;

  /** The kept render, or null when the fresh one is the answer. */
  const kept = fresh.error ? lastGood.current : null;

  // Memoised so the result keeps its identity between renders: callers derive their
  // own memos from it, and a fresh object per render would recompute all of them.
  return useMemo(
    () =>
      kept
        ? // The error is the *current* source's, reported even though the HTML — and
          // the variables read out of it — are the previous one's.
          { ...kept, error: fresh.error, stale: true }
        : { ...fresh, stale: false },
    [fresh, kept],
  );
}
